import { createContext, useContext, useState, useCallback } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import CalendlyInline from "@/components/CalendlyInline";
import { SITE } from "@/lib/data";
import { cn } from "@/lib/utils";

const Ctx = createContext(null);
export const useBookCall = () => useContext(Ctx);

export function BookCallProvider({ children }) {
    const [open, setOpen] = useState(false);
    const openModal = useCallback(() => setOpen(true), []);
    return (
        <Ctx.Provider value={{ openModal }}>
            {children}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="max-w-3xl max-h-[90vh] overflow-y-auto border-pa-line p-0 bg-white"
                    data-testid="book-call-modal"
                >
                    <DialogHeader className="px-6 pt-6 pb-4 border-b border-pa-line">
                        <DialogTitle className="font-display text-2xl text-pa-ink-900">
                            Book a discovery call
                        </DialogTitle>
                        <DialogDescription className="font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                            Working hours: {SITE.hours}
                        </DialogDescription>
                    </DialogHeader>
                    {open && (
                        <CalendlyInline
                            url={SITE.calendarUrl}
                            className="h-[62vh] min-h-[440px] bg-pa-blue-050"
                        />
                    )}
                    <div className="px-6 py-4 border-t border-pa-line flex items-center justify-between gap-4 flex-wrap">
                        <span className="text-sm text-pa-ink-500">
                            Calendar not loading?
                        </span>
                        <a
                            href={SITE.calendarUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="book-call-open-newtab"
                            className="font-mono text-sm text-pa-blue-700 underline underline-offset-4 hover:text-pa-blue-600"
                        >
                            Open the scheduling page →
                        </a>
                    </div>
                </DialogContent>
            </Dialog>
        </Ctx.Provider>
    );
}

// Primary CTA that opens the calendar modal. Motif: cut top-right corner.
export function BookCallButton({
    className = "",
    variant = "primary",
    children = "Book a discovery call",
    testId = "book-call-cta",
}) {
    const { openModal } = useBookCall();
    const base =
        "group relative inline-flex items-center gap-2 px-6 py-3 font-medium text-[15px] tracking-tight transition-colors duration-300 cut-tr";
    const styles =
        variant === "primary"
            ? "bg-pa-blue-600 text-white hover:bg-pa-blue-700"
            : variant === "onDark"
              ? "bg-white text-pa-ink-900 hover:bg-pa-blue-050"
              : "border border-pa-blue-600 text-pa-blue-700 hover:bg-pa-blue-050";
    return (
        <button
            type="button"
            onClick={openModal}
            data-testid={testId}
            className={cn(base, styles, className)}
        >
            {children}
            <span
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:translate-x-1"
            >
                →
            </span>
        </button>
    );
}
